import { hasKey, t } from '@app/i18n';
import {
  hopDeniedPhrase,
  trailInScreen,
  type AgentHop,
  type ErrorBody,
  type HopInScreen,
  type HopStatus,
} from './ConverseModel';
import s from './Conversar.module.css';

export interface HopPropsTrail {
  saltos: AgentHop[] | undefined;
  /** `true` desenha a etapa `a2a` — só a delegação a atravessa. */
  delegacao: boolean;
  /**
   * O desfecho da chamada inteira (`situacaoDaTroca`). É ele que dá a situação das
   * linhas `a2a` e `decisao` quando o executor não relata o salto correspondente —
   * o caso do laboratório. `null` = a chamada ainda está em voo.
   */
  desfecho?: HopStatus;
  /** Slug do especialista: o texto da linha `a2a` quando ela é derivada. */
  delegadoA?: string | null;
  /** Slug de quem chamou: entra na frase da recusa de delegação. */
  agente?: string | null;
  /**
   * O corpo da recusa, quando houve. É dele que sai o **porquê** de cada salto
   * negado — as duas listas do cruzamento e o código do agente/gateway.
   */
  recusa?: ErrorBody;
  /** A tool chamada: as listas da recusa se leem em relação a ela. */
  tool?: string;
}

/** Marca e prefixo acessível de cada situação. */
const MARK: Record<Exclude<HopStatus, null>, { simbolo: string; classe: string }> = {
  ok: { simbolo: '✓', classe: s.marcaOk },
  // `⊘` e âmbar, e não o ✕ vermelho: um salto negado é o produto funcionando —
  // alguém pediu, alguém decidiu recusar. Pintá-lo da mesma cor de uma queda de
  // rede faz a demonstração parecer defeito na hora exata em que ela dá certo.
  negado: { simbolo: '⊘', classe: s.marcaNegado },
  falhou: { simbolo: '✕', classe: s.marcaErro },
};

/**
 * A trilha do salto: `pessoa → agente → decisão`, uma linha por etapa, com a
 * situação e o tempo que o executor mediu. Na delegação entra o `a2a`, entre o
 * agente e a decisão.
 *
 * Duas linhas são **derivadas** quando o executor não relata o salto — `a2a` e
 * `decisao` —, porque nas duas o que aconteceu é sabido pelo desfecho da chamada,
 * e «não informado» ali seria falso.
 *
 * ## Duas perguntas, e só elas (decisão de 30/08, demo)
 *
 * Quem chamou quem, e qual foi a decisão. As etapas de transporte — `sts`,
 * `gateway`, `mcp` — saíram da tela: o middleware não relata duas delas, e o que
 * a lista mostrava no lab eram três «não informado» e um gateway não-ok sobre uma
 * chamada de 200. Elas continuam no JSON e na trilha de auditoria, e continuam
 * contando para o desfecho — o que não fazem mais é desenhar linha.
 *
 * ## Três estados, não dois
 *
 * Um salto pode ter **acontecido e seguido** (`ok`), **acontecido e sido
 * recusado** (`negado`) ou **não ter acontecido** (`falhou`). Enquanto o dado era
 * um booleano, os dois últimos eram a mesma linha vermelha: um DENY do PDP —
 * `ana` (perfil `cliente`) chamando `faturamento.consultar` — desenhava «Agente ✕
 * falhou: HTTP 403» e «Gateway ✕ falhou: HTTP 403», sobre um agente que só
 * repassou a negação e um gateway que negou porque é a função dele.
 *
 * ## O que a linha diz num salto negado
 *
 * Quem recusou e por quê, em vocabulário de produto: «negou: a pessoa não tem a
 * ferramenta no perfil». O detalhe que o middleware mandou — que no lab é «HTTP
 * 403» e nada mais — continua na linha, em segundo plano: ele é o que o executor
 * disse, e apagá-lo custaria a única pista de quem está depurando.
 *
 * ## A lista é desenhada inteira, sempre
 *
 * Etapa que o executor não relatou fica «não informado» em vez de sumir — é a
 * diferença entre «o middleware não conta esse pedaço» e «a chamada parou antes»,
 * e as duas levam a lugares opostos quando alguém está depurando o lab.
 */
export function HopTrail({
  saltos,
  delegacao,
  desfecho = null,
  delegadoA = null,
  agente = null,
  recusa,
  tool,
}: HopPropsTrail) {
  const linhas = trailInScreen({ saltos, delegacao, desfecho, delegadoA, recusa });

  return (
    <ul className={s.trilha} aria-label={t('conversation.trail.label')}>
      {linhas.map((linha) => (
        <HopRow
          key={linha.id}
          linha={linha}
          recusa={recusa}
          tool={tool}
          agente={agente}
          delegadoA={delegadoA}
        />
      ))}
    </ul>
  );
}

function HopRow({
  linha,
  recusa,
  tool,
  agente,
  delegadoA,
}: {
  linha: HopInScreen;
  recusa?: ErrorBody;
  tool?: string;
  agente?: string | null;
  delegadoA?: string | null;
}) {
  const relatado = linha.salto;
  const mark = linha.situacao ? MARK[linha.situacao] : { simbolo: '–', classe: s.marcaVazia };

  // Num salto negado o principal é a frase de produto e o detalhe do executor vem
  // atrás; nos outros o detalhe **é** a frase — o executor já a escreve legível
  // («ALLOW · a tool está no contrato e no perfil»), e reescrevê-la apagaria o
  // que ele disse.
  const negado = linha.situacao === 'negado';
  // `linha.texto` é a linha derivada: ela sabe o que dizer sem ter salto por trás,
  // e sem isto diria «não informado» sobre um veredito que a resposta já conta.
  //
  // `linha.repassou` é o passo que aconteceu e devolveu a negação de outro: o ✓ é
  // dele por direito, e a frase é o que impede o ✓ ao lado de «HTTP 403 em
  // …/delegar» de ser lido como sucesso da chamada.
  const principal = negado
    ? hopDeniedPhrase(linha.id, recusa ?? {}, tool ?? '', agente, delegadoA)
    : linha.repassou
      ? repassouANegacao()
      : (relatado?.detail ?? linha.texto ?? t('conversation.trail.unreported'));
  const secundario = negado || linha.repassou ? relatado?.detail : undefined;
  // Itálico apagado é o tom de «não se sabe». A linha derivada só o usa enquanto
  // não há desfecho — com veredito ela é informação como qualquer outra.
  const textTom = relatado || linha.situacao ? s.detalhe : s.detalheVazio;

  return (
    <li className={s.salto}>
      <span className={`${s.marca} ${mark.classe}`} aria-hidden="true">
        {mark.simbolo}
      </span>
      <span className={s.etapa}>{linha.rotulo}</span>
      <span className={textTom}>
        {/* O texto acessível carrega a situação: quem usa leitor de tela não vê o
            ✓/⊘/✕, e sem isto as linhas soariam iguais. */}
        <span className="sr-only">
          {/* Numa linha que repassou, a frase visível já diz o que aconteceu:
              «passou: repassou a negação» faria o leitor ouvir duas coisas em
              desacordo sobre o mesmo passo. */}
          {linha.repassou ? '' : prefixoAcessivel(linha.situacao ?? 'nao_informado')}
        </span>
        {principal}
        {secundario ? <span className={s.detalheSecundario}> · {secundario}</span> : null}
      </span>
      <span className={s.tempo}>{hopTime(relatado)}</span>
    </li>
  );
}

/** A frase de um passo que aconteceu e devolveu a negação decidida adiante. */
const repassouANegacao = (): string => t('conversation.refusal.verb.relayed');

/**
 * O que o leitor de tela ouve antes do texto do salto.
 *
 * `ok` e `falhou` precisam do prefixo porque o ✓/✕ é `aria-hidden` e sem ele as
 * linhas soariam iguais. `negado` **não** leva prefixo: a frase visível já
 * começa pelo verbo («negou:», «recusou:», «repassou a negação:»), e um «negado:»
 * na frente só faria o leitor ouvir a mesma palavra duas vezes.
 */
/**
 * Prefixo só para leitor de tela. `negado` e `nao_informado` não têm prefixo — a
 * frase deles já começa dizendo o que houve —, e por isso a função devolve
 * string vazia em vez de cair no catálogo.
 */
function prefixoAcessivel(situacao: string): string {
  const chave = `conversation.trail.spoken.${situacao}`;
  return hasKey(chave) ? t(chave) : '';
}

/**
 * O tempo do salto — só quando ele tem o que dizer.
 *
 * `0` é o salto que o backend infere da resposta, e não da medição do middleware:
 * escrever «0,0 ms» ali afirmaria uma medição que ninguém fez, sobre a etapa cuja
 * duração é justamente o que não se sabe.
 *
 * O corte é `< 0.05`, e não `<= 0`, porque a tela mostra uma casa decimal: `0.04`
 * é medição de verdade e **desenha «0.0 ms»**, que é a mesma frase que o parágrafo
 * acima existe para evitar — quem lê não distingue «rápido demais para esta régua»
 * de «ninguém mediu». Visto ao vivo em 30/08, nas linhas `Agente` e `Decisão` de
 * uma delegação. Acima do corte a exibição é honesta: `0.05` já sai «0.1 ms».
 */
const SMALLER_TIME_THAT_SCREEN_SHOWS = 0.05;

function hopTime(hop: AgentHop | null): string {
  if (!hop || hop.duration_ms < SMALLER_TIME_THAT_SCREEN_SHOWS) return '';
  return `${hop.duration_ms.toFixed(1)} ms`;
}
